import React, { useState, useEffect } from 'react';
import { message, Spin, Space, Button, Select } from 'antd';
import axios from 'axios';
import GenericTable from '../components/GenericTable';
import GenericModal from '../components/GenericModal ';
import './DefaultApiKeys.css';
import AppMenu from '../components/AppMenu';

const { Option } = Select;

const DefaultApiKeys = () => {
  const [defaultApiKeysData, setDefaultApiKeysData] = useState([]);
  const [filteredDefaultApiKeysData, setFilteredDefaultApiKeysData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingApiKey, setEditingApiKey] = useState(null);
  const [providers, setProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState(''); // State for selected provider filter
  const [providersLoading, setProvidersLoading] = useState(false);

  useEffect(() => {
    fetchDefaultApiKeys();
    fetchProviders();
  }, []);

  // Fetch default API keys data only
  const fetchDefaultApiKeys = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API}/api/apikeys/default`);
      
      // Format the data for display
      const formattedApiKeys = response.data.map(apiKey => ({
        ...apiKey,
        providerName: apiKey.provider?.name || 'N/A',
        statusDisplay: apiKey.isActive ? 'ACTIVE' : 'INACTIVE',
        createdAt: new Date(apiKey.createdAt).toLocaleDateString(),
        updatedAt: new Date(apiKey.updatedAt).toLocaleDateString(),
      }));
      
      setDefaultApiKeysData(formattedApiKeys);
      setFilteredDefaultApiKeysData(formattedApiKeys);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching default API keys:', error);
      message.error('Failed to load default API keys');
      setLoading(false);
    }
  };

  // Fetch list of providers
  const fetchProviders = async () => {
    setProvidersLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API}/api/providers`, {
        params: {
          page: 1,
          limit: 100, // Get all providers for dropdown
        }
      });
      
      // Handle both array response and paginated response
      const providersData = Array.isArray(response.data) ? response.data : response.data.data || response.data;
      setProviders(providersData || []);
    } catch (error) {
      console.error('Error fetching providers:', error);
      message.error('Failed to load providers');
      setProviders([]);
    } finally {
      setProvidersLoading(false);
    }
  };

  // Handle provider filter change
  const handleProviderChange = (value) => {
    setSelectedProvider(value);
    const filteredData = value
      ? defaultApiKeysData.filter((apiKey) => apiKey.providerId === value)
      : defaultApiKeysData;
    setFilteredDefaultApiKeysData(filteredData);
  };

  // Handle default API key creation (create with isDefault: true)
  const handleCreateDefaultApiKey = async (formData) => {
    try {
      const { name, key, subKey, region, providerId } = formData;
      await axios.post(
        `${process.env.REACT_APP_API}/api/apikeys`, 
        { name, key, subKey, region, providerId, isDefault: true } // Always create as default
      );
      message.success('Default API key created successfully');
      await fetchDefaultApiKeys();
      setModalVisible(false);
    } catch (error) {
      console.error('Error creating default API key:', error);
      if (error.response && error.response.data && error.response.data.message) {
        message.error(error.response.data.message);
      } else {
        message.error('Failed to create default API key');
      }
    }
  };

  // Toggle default API key activation status using the special endpoint
  const toggleDefaultKeyActivationStatus = async (apiKeyId, currentStatus) => {
    try {
      const newStatus = currentStatus ? 'INACTIVE' : 'ACTIVE';
      await axios.patch(
        `${process.env.REACT_APP_API}/api/apikeys/${apiKeyId}/default-activation-status`,
        {}
      );
      message.success(`Default API key status changed to ${newStatus}`);
      await fetchDefaultApiKeys();
    } catch (error) {
      console.error('Error toggling default API key status:', error);
      if (error.response && error.response.data && error.response.data.message) {
        message.error(error.response.data.message);
      } else {
        message.error('Failed to toggle default API key status');
      }
    }
  };

  // Handle edit API key - open edit modal with pre-filled data
  const handleEditApiKey = (record) => {
    setEditingApiKey(record);
    setEditModalVisible(true);
  };

  // Handle API key update
  const handleUpdateApiKey = async (formData) => {
    try {
      const { name, key, subKey, region } = formData;
      
      await axios.patch(
        `${process.env.REACT_APP_API}/api/apikeys/${editingApiKey.id}`,
        { name, key, subKey, region }
      );
      
      message.success('API key updated successfully');
      await fetchDefaultApiKeys();
      setEditModalVisible(false);
      setEditingApiKey(null);
    } catch (error) {
      console.error('Error updating API key:', error);
      if (error.response && error.response.data && error.response.data.message) {
        message.error(error.response.data.message);
      } else {
        message.error('Failed to update API key');
      }
    }
  };

  if (loading) {
    return <Spin size="large" style={{ display: 'block', margin: '20px auto' }} />;
  }

  return (
    <div style={{ display: 'flex' }}>
      <AppMenu />
      <div className="default-api-keys-page">
        <Space style={{ marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2>Default API Keys Management</h2>

          <Button type="primary" onClick={() => setModalVisible(true)}>
            Create Default API Key
          </Button>
        </Space>

        <div style={{ display: "flex", justifyContent: "end", marginBottom: 16 }}>
          <Select
            className="custom-select"
            placeholder="Filter by Provider"
            style={{ width: '300px', color: 'white' }}
            onChange={handleProviderChange}
            allowClear
            showSearch
            loading={providersLoading}
            optionFilterProp="children"
            filterOption={(input, option) =>
              option.children.toLowerCase().includes(input.toLowerCase())
            }
          >
            {providers.map((provider) => (
              <Option key={provider.id} value={provider.id}>
                {provider.name}
              </Option>
            ))}
          </Select>
        </div>

        <GenericTable
          data={filteredDefaultApiKeysData}
          searchKeys={['name', 'key', 'providerName']}
          sortableColumns={['name', 'statusDisplay', 'providerName', 'createdAt', 'updatedAt']}
          actions={['Toggle Status', 'Edit']}
          actionFunctions={[
            (record) => toggleDefaultKeyActivationStatus(record.id, record.isActive),
            (record) => handleEditApiKey(record),
          ]}
          filterableColumns={['providerName']}
          toggle="statusDisplay"
        />

        {/* Modal for Default API Key Creation */}
        <GenericModal
          isVisible={modalVisible}
          handleOk={handleCreateDefaultApiKey}
          handleCancel={() => setModalVisible(false)}
          title="Create New Default API Key"
          inputs={[
            { name: 'name', label: 'Name', type: 'text', require: true },
            { name: 'key', label: 'API Key', type: 'text', require: true },
            { name: 'subKey', label: 'Sub Key', type: 'text', require: false },
            { name: 'region', label: 'Region', type: 'text', require: false },
            {
              label: 'Provider',
              name: 'providerId',
              type: 'select',
              options: providers?.map(provider => ({ value: provider.id, label: provider.name })),
              placeholder: 'Select Provider',
              require: true,
              loading: providersLoading,
            },
          ]}
          actions={['Submit', 'Cancel']}
          actionFunctions={[
            (formData) => handleCreateDefaultApiKey(formData),
            () => setModalVisible(false),
          ]}
        />

        {/* Modal for Editing Default API Key */}
        <GenericModal
          isVisible={editModalVisible}
          handleOk={handleUpdateApiKey}
          handleCancel={() => {
            setEditModalVisible(false);
            setEditingApiKey(null);
          }}
          title="Edit Default API Key"
          predefinedData={editingApiKey ? {
            name: editingApiKey.name,
            key: editingApiKey.key,
            subKey: editingApiKey.subKey || '',
            region: editingApiKey.region || ''
          } : {}}
          inputs={[
            { name: 'name', label: 'Name', type: 'text', require: true },
            { name: 'key', label: 'API Key', type: 'text', require: true },
            { name: 'subKey', label: 'Sub Key', type: 'text', require: false },
            { name: 'region', label: 'Region', type: 'text', require: false },
          ]}
          actions={['Update', 'Cancel']}
          actionFunctions={[
            (formData) => handleUpdateApiKey(formData),
            () => {
              setEditModalVisible(false);
              setEditingApiKey(null);
            },
          ]}
        />
      </div>
    </div>
  );
};

export default DefaultApiKeys;